import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';
import { useTheme } from './ThemeContext';

/**
 * MultiSelectDropdown (core/ui) — wiederverwendbares Mehrfachauswahl-Dropdown
 * (Chip-Button + Badge + Checkbox-Liste, Click-outside, optional durchsuchbar,
 * optional „Alle auswählen / Alle abwählen"). Textunabhängig: alle Labels per Prop.
 *
 * Hinweis: Es gibt eine ältere, lm.*-i18n-gekoppelte Variante in components/lager/.
 * Diese core-Version ist die kanonische; die Lager-Variante kann später hierauf
 * migriert werden (Chore), ohne Lager-Dateien in diesem Worktree anzufassen.
 *
 * @param {string} label
 * @param {Array<string|{value,label}>} options
 * @param {string[]} selected
 * @param {(next:string[])=>void} onChange
 * @param {boolean} [searchable]
 * @param {string}  [width]              Tailwind-Breite des Panels (default w-56)
 * @param {string}  [searchPlaceholder]
 * @param {string}  [selectAllLabel]     wenn gesetzt -> „Alle auswählen" anzeigen
 * @param {string}  [clearAllLabel]      wenn gesetzt -> „Alle abwählen" anzeigen
 */
const MultiSelectDropdown = ({
  label, options, selected, onChange,
  searchable = false, width = 'w-56',
  searchPlaceholder = 'Suchen …', selectAllLabel, clearAllLabel,
}) => {
  const { isDarkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const norm = (options || []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const shown = q ? norm.filter((o) => String(o.label).toLowerCase().includes(q.toLowerCase())) : norm;
  const toggle = (val) => onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);
  const selectAll = () => onChange(norm.map((o) => o.value));
  const clearAll = () => onChange([]);
  const count = selected.length;
  const showBulk = Boolean(selectAllLabel || clearAllLabel) && norm.length > 0;

  const chipBase = 'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer select-none';
  const chipActive = isDarkMode ? 'bg-brand/25 border-brand/50 text-blue-200' : 'bg-brand/10 border-brand/40 text-brand';
  const chipInactive = isDarkMode ? 'bg-white/8 border-white/15 text-white/60 hover:bg-white/12' : 'bg-white border-slate-200 text-slate-600 hover:border-brand/30 hover:text-brand';
  const bulkBtn = isDarkMode ? 'text-blue-300 hover:text-blue-200' : 'text-brand hover:text-[#1D4ED8]';

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} className={`${chipBase} ${count > 0 ? chipActive : chipInactive}`}>
        <span>{label}</span>
        {count > 0 && <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isDarkMode ? 'bg-brand/40 text-white' : 'bg-brand text-white'}`}>{count}</span>}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`absolute z-40 mt-1 ${width} rounded-xl shadow-lg border overflow-hidden ${isDarkMode ? 'bg-slate-800 border-white/15' : 'bg-white border-slate-200'}`}>
          {searchable && (
            <div className={`p-2 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
              <div className="relative">
                <Search className={`absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder}
                  className={`w-full pl-7 pr-2 py-1.5 text-xs rounded-lg border outline-none ${isDarkMode ? 'bg-white/8 border-white/15 text-white placeholder-white/30' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'}`} />
              </div>
            </div>
          )}
          {showBulk && (
            <div className={`flex items-center justify-between px-3 py-1.5 text-[11px] border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
              {selectAllLabel ? <button type="button" onClick={selectAll} className={`font-medium ${bulkBtn}`}>{selectAllLabel}</button> : <span />}
              {clearAllLabel ? <button type="button" onClick={clearAll} className={`font-medium ${bulkBtn}`}>{clearAllLabel}</button> : <span />}
            </div>
          )}
          <div className="max-h-52 overflow-y-auto py-1">
            {shown.length === 0 ? (
              <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>—</div>
            ) : shown.map((o) => {
              const on = selected.includes(o.value);
              return (
                <button key={o.value} type="button" onClick={() => toggle(o.value)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left ${isDarkMode ? 'hover:bg-white/8 text-white/80' : 'hover:bg-slate-50 text-slate-700'}`}>
                  <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${on ? 'bg-brand border-brand' : (isDarkMode ? 'border-white/30' : 'border-slate-300')}`}>
                    {on && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <span className="truncate">{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
